import { defineStore } from 'pinia'
import signupLoginAPI from '~/apis/signupLoginApi'
import { useAuth } from '~/composables/useAuth'

export const useSignupLoginStore = defineStore('signupLogin', {
  state: () => {
    return {
      email: '',
    }
  },
  getters: {},
  actions: {
    async login(password: string) {
      const response = await signupLoginAPI.login(this.email, password)

      if (response.data.value?.token) {
        const { setToken, termsAcceptanceRequired, onboardingRequired } =
          useAuth()
        await setToken(response.data.value.token)
        // The Nuxt server login route updates the browser cookie via Set-Cookie,
        // but an already-mounted SPA can retain the previous reactive value until
        // it reloads. Synchronize it before route middleware runs so an accepted
        // user is not briefly redirected through /accept-terms.
        termsAcceptanceRequired.value = response.data.value
          .termsAcceptanceRequired
          ? 'true'
          : 'false'
        onboardingRequired.value = response.data.value.onboardingRequired
          ? 'true'
          : 'false'
      }

      if (!response.data.value?.mfaRequired) this.$state.email = ''
      return response
    },
    async verifyMFA(challengeToken: string, code: string) {
      const response = await signupLoginAPI.verifyMFA(challengeToken, code)
      if (response.data.value?.token) {
        const { setToken, termsAcceptanceRequired, onboardingRequired } =
          useAuth()
        await setToken(response.data.value.token)
        termsAcceptanceRequired.value = response.data.value
          .termsAcceptanceRequired
          ? 'true'
          : 'false'
        onboardingRequired.value = response.data.value.onboardingRequired
          ? 'true'
          : 'false'
      }
      this.$state.email = ''
      return response
    },
    async logout() {
      return signupLoginAPI.logout()
    },
    async signup(
      password: string,
      turnstileToken: string,
      inviteToken?: string,
    ) {
      // Don't clear this.email here: the plan-selection step (PlanSelector)
      // still needs it after the account is created.
      const response = await signupLoginAPI.signup(this.email, password, {
        turnstileToken,
        inviteToken,
      })

      // Log the new account in immediately so the plan-selection step (which
      // calls the now-authenticated /paddle/cardless-trial) carries the bearer
      // token. Without this the trial request would be anonymous and rejected.
      if (response?.token && !response?.emailVerificationRequired) {
        const { setToken, termsAcceptanceRequired, onboardingRequired } =
          useAuth()
        await setToken(response.token)
        termsAcceptanceRequired.value = 'true'
        onboardingRequired.value = response.onboardingRequired
          ? 'true'
          : 'false'
      }

      return response
    },
    async acceptTerms(termsVersion: string) {
      return signupLoginAPI.acceptTerms(termsVersion)
    },
    async forgotPassword(turnstileToken: string): Promise<{ message: string }> {
      try {
        // The email to use is already in this.email (bound from AuthForm.vue)
        const response = await signupLoginAPI.forgotPassword(
          this.email,
          turnstileToken,
        )
        // this.forgotPasswordSuccessMessage = response.message;
        return { message: response.message } // Propagate success message to AuthForm
      } catch (error) {
        console.error('Forgot password store action error:', error)
        // this.forgotPasswordError = error.message || 'An unexpected error occurred.';
        // Re-throw the error so AuthForm.vue can catch it and display it in errors.submit
        throw error
      } finally {
        // this.forgotPasswordLoading = false;
      }
    },
  },
})
